class FAQContactV3 {
  constructor(container) {
    this.container = container;
    this.currentCategory = null;
    this.setupElements();
    this.bindEvents();
    this.init();
  }

  setupElements() {
    this.filters = Array.from(this.container.querySelectorAll('.faq-contact-v3__filter'));
    this.items = Array.from(this.container.querySelectorAll('.faq-contact-v3__item'));
    this.questions = Array.from(this.container.querySelectorAll('.faq-contact-v3__question'));
  }

  bindEvents() {
    this.filters.forEach(filter => {
      filter.addEventListener('click', (e) => this.handleFilterClick(e));
    });

    this.questions.forEach(question => {
      question.addEventListener('click', (e) => this.handleQuestionClick(e));
    });

    // Handle anchor links to FAQ items
    this.setupAnchorLinks();
  }

  init() {
    // Set current category to the first active filter
    const activeFilter = this.filters.find(f => f.classList.contains('faq-contact-v3__filter--active'));
    if (activeFilter) {
      this.currentCategory = activeFilter.dataset.category;
    }

    // Close all FAQs by default
    this.items.forEach(item => {
      item.classList.remove('faq-contact-v3__item--active');
      const question = item.querySelector('.faq-contact-v3__question');
      if (question) {
        question.setAttribute('aria-expanded', 'false');
      }
      const answer = item.querySelector('.faq-contact-v3__answer');
      if (answer) {
        answer.setAttribute('aria-hidden', 'true');
        answer.style.height = '0px';
        answer.style.opacity = '0';
      }
    });

    // Filter items based on initial category
    if (this.currentCategory) {
      this.filterItems(this.currentCategory);
    }
  }

  handleFilterClick(e) {
    const button = e.currentTarget;
    const category = button.dataset.category;

    if (category === this.currentCategory) return;

    this.currentCategory = category;

    this.filters.forEach(filter => {
      const isActive = filter.dataset.category === category;
      filter.classList.toggle('faq-contact-v3__filter--active', isActive);
      filter.setAttribute('aria-selected', isActive ? 'true' : 'false');
    });

    this.filterItems(category);
  }

  filterItems(category) {
    this.items.forEach(item => {
      const itemCategory = item.dataset.category;
      const shouldShow = itemCategory === category;

      if (shouldShow) {
        item.removeAttribute('data-hidden');
        item.style.display = '';
      } else {
        item.setAttribute('data-hidden', 'true');
        item.style.display = 'none';

        if (item.classList.contains('faq-contact-v3__item--active')) {
          this.closeItem(item);
        }
      }
    });
  }

  handleQuestionClick(e) {
    const button = e.currentTarget;
    const item = button.closest('.faq-contact-v3__item');

    if (!item) return;

    const isActive = item.classList.contains('faq-contact-v3__item--active');

    if (isActive) {
      this.closeItem(item);
    } else {
      this.openItem(item);
    }
  }

  openItem(item) {
    item.classList.add('faq-contact-v3__item--active');

    const question = item.querySelector('.faq-contact-v3__question');
    if (question) {
      question.setAttribute('aria-expanded', 'true');
    }

    const answer = item.querySelector('.faq-contact-v3__answer');
    if (answer) {
      answer.setAttribute('aria-hidden', 'false');

      // Calculate and set height for smooth animation
      const content = answer.querySelector('.faq-contact-v3__answer-content');
      if (content) {
        const contentHeight = content.scrollHeight;
        answer.style.height = contentHeight + 'px';
        answer.style.opacity = '1';
      }
    }
  }

  closeItem(item) {
    item.classList.remove('faq-contact-v3__item--active');

    const question = item.querySelector('.faq-contact-v3__question');
    if (question) {
      question.setAttribute('aria-expanded', 'false');
    }

    const answer = item.querySelector('.faq-contact-v3__answer');
    if (answer) {
      answer.setAttribute('aria-hidden', 'true');

      // Reset height and opacity for smooth close animation
      answer.style.height = '0px';
      answer.style.opacity = '0';
    }
  }

  setupAnchorLinks() {
    // Listen to all clicks on the document
    document.addEventListener('click', (e) => {
      const link = e.target.closest('a[href*="#"]');
      if (!link) return;

      const href = link.getAttribute('href');
      if (!href || !href.includes('#')) return;

      // Extract the anchor ID
      const anchorId = href.split('#')[1];
      if (!anchorId) return;

      // Check if there's a FAQ item with this ID
      const targetFaqItem = document.getElementById(anchorId);
      if (!targetFaqItem || !targetFaqItem.classList.contains('faq-contact-v3__item')) return;

      // Prevent default behavior
      e.preventDefault();

      // Get the category of the target FAQ item
      const targetCategory = targetFaqItem.dataset.category;

      // If the item is in a different category, switch to it first
      if (this.currentCategory !== targetCategory) {
        // Find the matching category filter
        const targetFilter = this.filters.find(f => f.dataset.category === targetCategory);
        if (targetFilter) {
          targetFilter.click();
        }
      }

      // Wait for filter animation to complete, then scroll and open
      setTimeout(() => {
        // Scroll to the FAQ item with offset
        const offset = 100; // Offset from top
        const elementPosition = targetFaqItem.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - offset;

        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });

        // Open the FAQ item if it's not already open
        if (!targetFaqItem.classList.contains('faq-contact-v3__item--active')) {
          this.openItem(targetFaqItem);
        }
      }, this.currentCategory !== targetCategory ? 300 : 0);
    });
  }

  destroy() {
    this.filters.forEach(filter => {
      filter.removeEventListener('click', this.handleFilterClick);
    });

    this.questions.forEach(question => {
      question.removeEventListener('click', this.handleQuestionClick);
    });
  }
}

document.addEventListener('DOMContentLoaded', () => {
  const sections = document.querySelectorAll('[data-section-type="faq-contact-v3"]');
  sections.forEach(section => {
    new FAQContactV3(section);
  });
});

document.addEventListener('shopify:section:load', (event) => {
  if (event.target.querySelector('[data-section-type="faq-contact-v3"]')) {
    new FAQContactV3(event.target);
  }
});

document.addEventListener('shopify:section:unload', (event) => {
  const section = event.target.querySelector('[data-section-type="faq-contact-v3"]');
  if (section && section.faqContactInstance) {
    section.faqContactInstance.destroy();
  }
});
